/**
 * On-screen presentation: one model normalised to a convenient size and orbited with the
 * mouse.
 *
 * Real-world scale is deliberately abandoned here. A 300 mm bracket and a 2 mm grub screw
 * both need to fill a similar fraction of the window to be useful on a monitor, and there
 * is nothing on a desktop to judge true size against anyway. VR is where scale is honest.
 *
 * One model, too, for the same reason. A roomful at true size is exactly what VR is for;
 * on a monitor, normalising is what makes a model legible, and there is no honest way to
 * normalise several at once — whatever scale suits the bracket loses the grub screw. So
 * this shows the selection, and the room is left to the headset.
 */

import { MOUSE, type Vector3 } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import {
  boundingRadius,
  boundsSize,
  computeCameraDistance,
  computeFitScale,
  orbitOffset,
} from '../domain/scaling.js';
import type { PresentationMode, SceneManager, SceneModel } from './sceneManager.js';

/** Longest edge of the model after normalisation, in scene units. */
const NOMINAL_SIZE = 1;

/** `PointerEvent.button` for the wheel pressed as a button. */
const MIDDLE_BUTTON = 1;

export class DesktopMode implements PresentationMode {
  private readonly controls: OrbitControls;
  private model: SceneModel | undefined;

  constructor(private readonly scene: SceneManager) {
    this.controls = new OrbitControls(scene.camera, scene.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 0.05;
    this.controls.maxDistance = 50;
    this.controls.enabled = false;

    // Middle-drag pans, which is what every other 3D tool does and so what a hand already
    // on the wheel expects. three's default puts dolly there, which only duplicates what
    // rolling the wheel does anyway — so the button was doing the one job it was not
    // needed for. Right-drag keeps panning as well; there is no reason to take away a
    // habit anyone may already have formed.
    this.controls.mouseButtons = {
      LEFT: MOUSE.ROTATE,
      MIDDLE: MOUSE.PAN,
      RIGHT: MOUSE.PAN,
    };

    scene.renderer.domElement.addEventListener('pointerdown', this.suppressAutoscroll);
  }

  /**
   * Stop the browser hijacking a middle-button press.
   *
   * Firefox and Chrome open their autoscroll widget on middle-click, which grabs the
   * pointer and leaves the drag doing nothing. `OrbitControls` does not prevent it,
   * having had no need to while middle-drag was a dolly nobody used.
   *
   * `preventDefault` only suppresses the browser's own behaviour; the event still reaches
   * the controls, which is why this can be a plain second listener rather than something
   * that has to interpose itself.
   */
  private readonly suppressAutoscroll = (event: PointerEvent): void => {
    if (event.button === MIDDLE_BUTTON) event.preventDefault();
  };

  enter(): void {
    this.controls.enabled = true;
    this.scene.setEnvironmentVisible(false);
    this.showOnlyTheModel();
    this.applyPlacement();
    this.resetView();
  }

  exit(): void {
    this.controls.enabled = false;
  }

  update(): void {
    // Damping means the camera keeps easing after the mouse stops, so this has to run
    // every frame, not just on input.
    this.controls.update();
  }

  /** Tell the mode which model it is framing. Safe to call while inactive. */
  setModel(model: SceneModel | undefined): void {
    this.model = model;
    if (!this.controls.enabled) return;

    this.showOnlyTheModel();
    this.applyPlacement();
    this.resetView();
  }

  /**
   * Hide everything else in the scene.
   *
   * The room a headset user arranged is still loaded and still positioned where they left
   * it; it just has no business being on screen, where it would be a scattering of models
   * at metre scale around a model normalised to fill the window.
   */
  private showOnlyTheModel(): void {
    this.scene.setVisible(this.model ? [this.model.id] : []);
  }

  /**
   * Normalise the model to a fixed nominal size, centred at the world origin.
   *
   * This overwrites whatever transform the model was carrying, including a position it
   * was given in VR. `VrMode` keeps its own record of the room's arrangement and puts it
   * back on entry, precisely so that this is free to do so.
   */
  private applyPlacement(): void {
    const model = this.model;
    if (!model) {
      this.scene.frameLighting();
      return;
    }

    const { root } = model;
    root.position.set(0, 0, 0);
    root.quaternion.identity();
    root.scale.setScalar(computeFitScale(model.bounds, NOMINAL_SIZE));
    root.updateMatrixWorld(true);

    this.scene.frameLighting();
  }

  /** Return the camera to a three-quarter view that frames the whole model. */
  resetView(): void {
    const { camera } = this.scene;

    this.discardMomentum();

    const bounds = this.model?.bounds;
    if (!bounds) {
      this.controls.target.set(0, 0, 0);
      camera.position.set(0, 0.6, 2.2);
      this.settle();
      return;
    }

    const scale = computeFitScale(bounds, NOMINAL_SIZE);
    const [, heightMm] = boundsSize(bounds);
    const radius = boundingRadius(bounds) * scale;

    // The model stands on y = 0, so its middle — the natural thing to look at — is half
    // its height up.
    this.controls.target.set(0, (heightMm * scale) / 2, 0);

    const distance = computeCameraDistance(radius, camera.fov, camera.aspect);

    placeOnOrbit(camera.position, this.controls.target, distance);
    camera.near = Math.max(distance / 1000, 0.001);
    camera.far = distance * 100;
    camera.updateProjectionMatrix();

    this.settle();
  }

  /**
   * Throw away any leftover motion from the user's last drag.
   *
   * Damping decays the pending rotation asymptotically, so it is never quite zero, and
   * whatever remains keeps being applied for a second or two afterwards — which would
   * drag the camera straight back off the view we are about to set. Running one
   * undamped update discards it outright, which damping-off is defined to do.
   */
  private discardMomentum(): void {
    this.controls.enableDamping = false;
    this.controls.update();
  }

  /** Apply a camera change exactly, then hand easing back to the damping. */
  private settle(): void {
    this.controls.update();
    this.controls.enableDamping = true;
  }

  /** Reframe after a window resize, since the limiting field of view may have changed. */
  handleResize(): void {
    if (this.controls.enabled) this.controls.update();
  }

  dispose(): void {
    this.scene.renderer.domElement.removeEventListener(
      'pointerdown',
      this.suppressAutoscroll,
    );
    this.controls.dispose();
  }
}

/**
 * Stand the camera back from the target along the shared three-quarter view.
 *
 * The direction itself is `orbitOffset` in `domain/scaling.ts`, so that the flat view and
 * the thumbnails in the folded sidebar cannot come to disagree about what a model looks
 * like — a picture of a model that does not match the model when you open it is worse than
 * no picture.
 */
function placeOnOrbit(out: Vector3, target: Vector3, distance: number): void {
  out.set(...orbitOffset(distance)).add(target);
}
